import { useEffect, useRef } from 'react';
import { reportSignupConversion } from './signup-conversion-pixels';

// Cookie consent (window.mscc) is injected asynchronously by the universal header, so the
// consent gate can briefly be unready when `cid` first appears. Re-check for a bounded window
// so a genuine customer-created conversion is not permanently dropped by that load race.
const CONSENT_RETRY_INTERVAL_MS = 2000;
const CONSENT_RETRY_WINDOW_MS = 30000;
// Remember the reported cid for the browser session so a page reload with the same cid
// doesn't double-count the conversion.
const SESSION_FIRED_CID_KEY = 'pmax_signup_conversion_fired_cid';

function getSessionFiredCid() {
  try {
    return window.sessionStorage.getItem(SESSION_FIRED_CID_KEY) || '';
  } catch (e) {
    return '';
  }
}

function setSessionFiredCid(value) {
  try {
    window.sessionStorage.setItem(SESSION_FIRED_CID_KEY, value);
  } catch (e) {
    // ignore storage failures (e.g. private mode / blocked storage)
  }
}

// Fires the signup conversion pixels once per customer, when the customer is created
// (a non-empty `cid` becomes available) and the governance consent gates allow it.
// `cid` is passed in so this hook stays free of any scope/router dependency.
export function useReportSignupConversion(cid) {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) {
      return undefined;
    }
    const normalizedCid = cid ? `${cid}`.trim() : '';
    if (!normalizedCid) {
      return undefined;
    }
    // Already reported for this customer earlier in the session (e.g. before a reload).
    if (getSessionFiredCid() === normalizedCid) {
      hasFiredRef.current = true;
      return undefined;
    }

    // reportSignupConversion applies the GPC + cookie-consent gates before firing.
    const tryReport = () => {
      if (hasFiredRef.current) {
        return true;
      }
      if (reportSignupConversion()) {
        hasFiredRef.current = true;
        setSessionFiredCid(normalizedCid);
        return true;
      }
      return false;
    };

    if (tryReport()) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (tryReport()) {
        clearInterval(intervalId);
      }
    }, CONSENT_RETRY_INTERVAL_MS);
    const stopId = setTimeout(() => clearInterval(intervalId), CONSENT_RETRY_WINDOW_MS);

    return () => {
      clearInterval(intervalId);
      clearTimeout(stopId);
    };
  }, [cid]);
}
